import type * as api from '@gitbook/api-client';

import { CHANGE_TYPES } from './constants';

/** How the counts of a change request are shown: its block and library counts, or only "large". */
export type ChangeCountsView = 'counted' | 'large';

type BlocksOmittedReason = NonNullable<api.ChangeRequestChangeCounts['blocksOmittedReason']>;

/** Mirrors `MAX_CHANGE_COUNT_DOCUMENTS` in `backend-spaces/src/changeRequestChangeCounts.ts`. */
const CHANGE_COUNT_PAGE_LIMIT = 50;

const BLOCKS_OMITTED_REASONS: Record<BlocksOmittedReason, string> = {
    'too-many-pages': `More than ${CHANGE_COUNT_PAGE_LIMIT} pages changed.`,
    // The backend limit covers every block of the changed pages, not only the changed ones.
    'too-many-blocks': 'The changed pages are too long to compare.',
    timeout: 'Counting took too long.',
};

/** How to show the counts: whatever the reason, omitted block counts give no bound on changes. */
export function getChangeCountsView(changeCounts: api.ChangeRequestChangeCounts): ChangeCountsView {
    return changeCounts.blocksOmittedReason ? 'large' : 'counted';
}

/** Why the block counts were omitted, as a sentence; empty when they were counted. */
export function describeBlocksOmittedReason(changeCounts: api.ChangeRequestChangeCounts): string {
    return changeCounts.blocksOmittedReason
        ? BLOCKS_OMITTED_REASONS[changeCounts.blocksOmittedReason]
        : '';
}

/**
 * Where the counts come from: blocks when any block changed, otherwise pages (blocks too large to
 * count, or only page titles or order changed).
 */
export function getChangeCountsSource(
    changeCounts: api.ChangeRequestChangeCounts
): 'blocks' | 'pages' {
    return changeCounts.blocks && hasChangeCounts(changeCounts.blocks) ? 'blocks' : 'pages';
}

/** Combine library changes with block magnitude, falling back to page counts. */
export function combineChangeCounts(
    changeCounts: api.ChangeRequestChangeCounts
): api.ChangeRequestChangeCountsByType {
    const contentCounts =
        changeCounts.blocks && hasChangeCounts(changeCounts.blocks)
            ? changeCounts.blocks
            : changeCounts.pages;

    return {
        added: contentCounts.added + changeCounts.library.added,
        modified: contentCounts.modified + changeCounts.library.modified,
        deleted: contentCounts.deleted + changeCounts.library.deleted,
    };
}

/** Whether any semantic entity count is non-zero. */
export function hasChangeCounts(counts: api.ChangeRequestChangeCountsByType): boolean {
    return counts.added > 0 || counts.modified > 0 || counts.deleted > 0;
}

/** Breakdown of the counts, such as "51 added, 5 modified, 1 deleted". */
export function describeChangeCounts(counts: api.ChangeRequestChangeCountsByType): string {
    return CHANGE_TYPES.filter((type) => counts[type] > 0)
        .map((type) => `${counts[type]} ${type}`)
        .join(', ');
}
